import * as React from 'react';
import {
  type ShortCode,
  type EnrichmentContext,
  type GravityFormResponse,
  fetchGravityForms,
} from '@highereducation/wordpress';
import { GravityFormClient } from './GravityFormClient';
import type {
  GravityFormNode,
  GravityFormShortcodeProps,
  FormConfirmation,
} from './types';

export { GravityFormClient } from './GravityFormClient';
export type {
  GravityFormShortcodeProps,
  GravityFormClientProps,
  GravityFormField,
  GravityFormNode,
} from './types';

/** `false` / `"false"` → suppressed. */
const isSuppressed = (value: string | boolean | undefined): boolean =>
  value === false || value === 'false';

/**
 * GravityFormShortcode — `[hen-ui-gravity-form]`
 *
 * Renders and submits a Gravity Form natively in the HEN content pipeline
 * (HEN tokens, no Sonic styles). Data is fetched per shortcode instance via
 * `getEnrichedData` → `fetchGravityForms(cmsUrl, [id])`, mirroring the
 * CategoryHub enrichment pattern.
 *
 * @example
 * [hen-ui-gravity-form id="4"]
 * [hen-ui-gravity-form id="4" title="false" description="false"]
 */
export const GravityFormShortcode: ShortCode<
  GravityFormShortcodeProps,
  GravityFormResponse | null
> = {
  getEnrichedData: async (
    _content: string,
    context?: EnrichmentContext,
  ): Promise<GravityFormResponse | null> => {
    const id = context?.attrs?.id?.trim();
    const cmsUrl = context?.config?.cmsUrl;
    if (!id || !cmsUrl) return null;

    const formId = Number(id);
    if (!Number.isInteger(formId)) return null;

    const response = await fetchGravityForms(cmsUrl, [formId]);
    if (!response) return null;

    // Consent copy is CMS-authored HTML and legally sensitive, so it is
    // sanitized here at the server enrichment boundary before being cached and
    // serialized to the client renderer. The dynamic import keeps sanitize-html
    // (a Node-oriented dependency) out of every client-reachable module graph.
    const { sanitizeConsentHtml } = await import(
      './fields/sanitizeConsentHtml'
    );
    return {
      ...response,
      nodes: response.nodes.map((node) => ({
        ...node,
        formFields: {
          ...node.formFields,
          nodes: node.formFields.nodes.map((field) =>
            field.type === 'CONSENT' && typeof field.description === 'string'
              ? {
                  ...field,
                  description: sanitizeConsentHtml(field.description),
                }
              : field,
          ),
        },
      })),
    };
  },

  component: ({ id, title, description, enrichments }) => {
    const response = enrichments?.data ?? null;
    const formId = Number(id);
    if (!Number.isInteger(formId)) return null;

    if (!response) {
      console.warn(
        `GravityFormShortcode: no Gravity Form data returned for id="${id}" (enrichment miss).`,
      );
      return null;
    }

    const node = (response.nodes as unknown as GravityFormNode[]).find(
      (form) => form.databaseId === formId,
    );
    if (!node) {
      console.warn(
        `GravityFormShortcode: configured form id="${id}" resolved to no form node.`,
      );
      return null;
    }

    return (
      <GravityFormClient
        id={node.databaseId}
        title={isSuppressed(title) ? undefined : node.title}
        description={isSuppressed(description) ? undefined : node.description}
        fields={node.formFields?.nodes ?? []}
        confirmations={(node.confirmations as FormConfirmation[]) ?? []}
        submitButton={node.submitButton}
        hasHoneypot={node.hasHoneypot}
        submit={response.submit}
      />
    );
  },
};
